import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Settings,
  User,
  Lock,
  Bell,
  Globe,
  Shield,
  Camera,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Users,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Moon,
  Sun,
  Monitor,
  Trash2,
  Upload,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { userAPI } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarInitials,
} from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { getPhotoUrl } from "@/lib/utils";

// Validation schemas
const profileSchema = z.object({
  prenom: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  nom: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Format d'email invalide"),
  telephone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^(\+212|0)[5-7][0-9]{8}$/.test(val),
      "Format de téléphone marocain invalide (+212XXXXXXXXX ou 0XXXXXXXXX)"
    ),
  adresse: z.string().optional(),
  situation_familiale: z
    .enum(["celibataire", "marie", "divorce", "veuf", "pacse"])
    .optional(),
  nombre_enfants: z
    .string()
    .optional()
    .refine(
      (val) => !val || (!isNaN(Number(val)) && Number(val) >= 0),
      "Le nombre d'enfants doit être un nombre positif"
    ),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Mot de passe actuel requis"),
    newPassword: z
      .string()
      .min(8, "Le nouveau mot de passe doit contenir au moins 8 caractères")
      .regex(/[A-Z]/, "Doit contenir au moins une majuscule")
      .regex(/[a-z]/, "Doit contenir au moins une minuscule")
      .regex(/[0-9]/, "Doit contenir au moins un chiffre")
      .regex(/[^A-Za-z0-9]/, "Doit contenir au moins un caractère spécial"),
    confirmPassword: z.string().min(1, "Confirmation requise"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

const situationFamiliale = [
  { value: "celibataire", label: "Célibataire" },
  { value: "marie", label: "Marié(e)" },
  { value: "divorce", label: "Divorcé(e)" },
  { value: "veuf", label: "Veuf/Veuve" },
  { value: "pacse", label: "Pacsé(e)" },
];

const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  if (strength <= 2)
    return { level: "Faible", color: "bg-red-500", percent: 40 };
  if (strength <= 3)
    return { level: "Moyen", color: "bg-yellow-500", percent: 60 };
  if (strength <= 4) return { level: "Bon", color: "bg-blue-500", percent: 80 };
  return { level: "Excellent", color: "bg-green-500", percent: 100 };
};

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [theme, setTheme] = useState("system");
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    training: true,
    leave: true,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  console.log(user);

  const profileForm = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      prenom: user?.prenom || "",
      nom: user?.nom || "",
      email: user?.email || "",
      telephone: user?.telephone || "",
      adresse: user?.adresse || "",
      situation_familiale: user?.situation_familiale || "",
      nombre_enfants: user?.nombre_enfants?.toString() || "0",
    },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { watch: watchPassword } = passwordForm;
  const newPassword = watchPassword("newPassword");
  const passwordStrength = newPassword
    ? getPasswordStrength(newPassword)
    : null;

  useEffect(() => {
    if (user) {
      profileForm.reset({
        prenom: user.prenom || "",
        nom: user.nom || "",
        email: user.email || "",
        telephone: user.telephone || "",
        adresse: user.adresse || "",
        situation_familiale: user.situation_familiale || "",
        nombre_enfants: user.nombre_enfants?.toString() || "0",
      });
    }
  }, [user, profileForm]);

  const handleProfileUpdate = async (data) => {
    try {
      // Convert nombre_enfants back to number
      const updateData = {
        ...data,
        nombre_enfants: data.nombre_enfants ? parseInt(data.nombre_enfants) : 0,
      };

      const response = await userAPI.updateProfile(updateData);

      toast({
        title: "Succès",
        description: "Profil mis à jour avec succès",
      });

      setIsEditing(false);
      // Update the auth context with new data
      updateUser(response.data.data);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          "Erreur lors de la mise à jour du profil",
        variant: "destructive",
      });
    }
  };

  const handlePasswordUpdate = async (data) => {
    try {
      await userAPI.updatePassword(data);

      toast({
        title: "Succès",
        description: "Mot de passe mis à jour avec succès",
      });

      setShowPasswordDialog(false);
      passwordForm.reset();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          "Erreur lors de la mise à jour du mot de passe",
        variant: "destructive",
      });
    }
  };

  const handleNotificationUpdate = async (key, value) => {
    const newNotifications = { ...notifications, [key]: value };
    setNotifications(newNotifications);

    try {
      await userAPI.updateNotifications(newNotifications);
      toast({
        title: "Succès",
        description: "Préférences de notification mises à jour",
      });
    } catch (error) {
      // Revert on error
      setNotifications(notifications);
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour des notifications",
        variant: "destructive",
      });
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un fichier image valide",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erreur",
        description: "L'image est trop volumineuse (5MB maximum)",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingPhoto(true);

    try {
      // Compress and resize image before converting to base64
      const compressedFile = await compressImage(file);

      // Convert to base64
      const base64String = await fileToBase64(compressedFile);

      // Set preview
      const objectUrl = URL.createObjectURL(compressedFile);
      setPhotoPreview(objectUrl);

      // Update profile with base64 data
      const updateData = {
        photo_base64: base64String,
      };

      const response = await userAPI.updateProfile(updateData);

      // Update the user context with the new photo
      updateUser(response.data.data);

      toast({
        title: "Succès",
        description: "Photo de profil mise à jour avec succès",
      });

      // Clear preview after successful upload
      setTimeout(() => {
        setPhotoPreview(null);
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          "Erreur lors du téléchargement de la photo",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Compress image to reduce base64 size
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Resize image to maximum 400x400 while maintaining aspect ratio
        const maxSize = 400;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg",
          0.8 // 80% quality
        );
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDeletePhoto = async () => {
    setIsUploadingPhoto(true);

    try {
      // Send null photo_base64 to remove the photo
      const updateData = {
        photo_base64: null,
      };

      const response = await userAPI.updateProfile(updateData);

      // Update the user context to remove the photo
      updateUser(response.data.data);

      toast({
        title: "Succès",
        description: "Photo de profil supprimée avec succès",
      });

      // Clear preview
      setPhotoPreview(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          "Erreur lors de la suppression de la photo",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const getPhotoUrl = (photoData) => {
    if (!photoData) return null;

    // If it's already a base64 string, return it directly
    if (photoData.startsWith("data:image/")) {
      return photoData;
    }

    // If it's a file path, construct the URL (fallback for older data)
    const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
    return `${baseUrl}/uploads/${photoData}`;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "rh":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "manager":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "rh":
        return "Ressources Humaines";
      case "manager":
        return "Manager";
      case "employe":
        return "Employé";
      default:
        return role;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className=" mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Paramètres
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Gérez votre profil et vos préférences
              </p>
            </div>
            <Settings className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        {/* Main Content Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="border-b border-gray-200 dark:border-gray-700 px-6">
              <TabsList className="grid w-full grid-cols-4 bg-transparent p-0 h-auto">
                <TabsTrigger
                  value="profile"
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <User className="h-4 w-4" />
                  Profil
                </TabsTrigger>
                <TabsTrigger
                  value="security"
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  Sécurité
                </TabsTrigger>
                <TabsTrigger
                  value="notifications"
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <Bell className="h-4 w-4" />
                  Notifications
                </TabsTrigger>
                <TabsTrigger
                  value="preferences"
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <Globe className="h-4 w-4" />
                  Préférences
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6 p-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Informations personnelles
                      </CardTitle>
                      <CardDescription>
                        Gérez vos informations de profil
                      </CardDescription>
                    </div>
                    <Button
                      variant={isEditing ? "destructive" : "outline"}
                      onClick={() => {
                        if (isEditing) {
                          profileForm.reset();
                        }
                        setIsEditing(!isEditing);
                      }}
                    >
                      {isEditing ? (
                        <>
                          <X className="h-4 w-4 mr-2" />
                          Annuler
                        </>
                      ) : (
                        <>
                          <Edit3 className="h-4 w-4 mr-2" />
                          Modifier
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profile Header */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <Avatar className="h-20 w-20">
                        {user?.photo_profil ? (
                          <AvatarImage
                            src={getPhotoUrl(user.photo_profil)}
                            alt={`${user?.prenom} ${user?.nom}`}
                          />
                        ) : null}
                        <AvatarFallback className="bg-transparent">
                          <AvatarInitials
                            name={`${user?.prenom} ${user?.nom}`}
                            email={user?.email}
                          />
                        </AvatarFallback>
                      </Avatar>
                      {isEditing && (
                        <>
                          <input
                            type="file"
                            id="photo-upload"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                          <Button
                            size="sm"
                            className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
                            onClick={() =>
                              document.getElementById("photo-upload").click()
                            }
                            disabled={isUploadingPhoto}
                          >
                            {isUploadingPhoto ? (
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <Camera className="h-4 w-4" />
                            )}
                          </Button>
                          {user?.photo_profil && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute -bottom-2 -left-2 h-8 w-8 rounded-full"
                              onClick={handleDeletePhoto}
                              disabled={isUploadingPhoto}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {user?.prenom} {user?.nom}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {user?.email}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={getRoleBadgeColor(user?.role)}>
                          {getRoleLabel(user?.role)}
                        </Badge>
                        <Badge variant="outline">{user?.numero_employe}</Badge>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Profile Form */}
                  <Form {...profileForm}>
                    <form
                      onSubmit={profileForm.handleSubmit(handleProfileUpdate)}
                      className="space-y-6"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={profileForm.control}
                          name="prenom"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Prénom
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  disabled={!isEditing}
                                  className={
                                    !isEditing
                                      ? "bg-gray-50 dark:bg-gray-800"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="nom"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Nom
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  disabled={!isEditing}
                                  className={
                                    !isEditing
                                      ? "bg-gray-50 dark:bg-gray-800"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="email"
                                  disabled={!isEditing}
                                  className={
                                    !isEditing
                                      ? "bg-gray-50 dark:bg-gray-800"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="telephone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Téléphone
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="+212 6XX XXX XXX"
                                  disabled={!isEditing}
                                  className={
                                    !isEditing
                                      ? "bg-gray-50 dark:bg-gray-800"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="situation_familiale"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Situation familiale
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={!isEditing}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      !isEditing
                                        ? "bg-gray-50 dark:bg-gray-800"
                                        : ""
                                    }
                                  >
                                    <SelectValue placeholder="Sélectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {situationFamiliale.map((situation) => (
                                    <SelectItem
                                      key={situation.value}
                                      value={situation.value}
                                    >
                                      {situation.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="nombre_enfants"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Nombre d'enfants
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  min="0"
                                  disabled={!isEditing}
                                  className={
                                    !isEditing
                                      ? "bg-gray-50 dark:bg-gray-800"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={profileForm.control}
                        name="adresse"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              Adresse
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Votre adresse complète..."
                                disabled={!isEditing}
                                className={
                                  !isEditing
                                    ? "bg-gray-50 dark:bg-gray-800"
                                    : ""
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {isEditing && (
                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            className="flex items-center gap-2"
                          >
                            <Save className="h-4 w-4" />
                            Sauvegarder
                          </Button>
                        </div>
                      )}
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6 p-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Sécurité du compte
                  </CardTitle>
                  <CardDescription>
                    Gérez la sécurité de votre compte
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Lock className="h-5 w-5 text-gray-500" />
                      <div>
                        <h4 className="font-medium">Mot de passe</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Dernière modification il y a 30 jours
                        </p>
                      </div>
                    </div>
                    <Dialog
                      open={showPasswordDialog}
                      onOpenChange={setShowPasswordDialog}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline">Modifier</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Changer le mot de passe</DialogTitle>
                          <DialogDescription>
                            Choisissez un mot de passe fort pour sécuriser votre
                            compte
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...passwordForm}>
                          <form
                            onSubmit={passwordForm.handleSubmit(
                              handlePasswordUpdate
                            )}
                            className="space-y-4"
                          >
                            <FormField
                              control={passwordForm.control}
                              name="currentPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Mot de passe actuel</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        {...field}
                                        type={
                                          showCurrentPassword
                                            ? "text"
                                            : "password"
                                        }
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3"
                                        onClick={() =>
                                          setShowCurrentPassword(
                                            !showCurrentPassword
                                          )
                                        }
                                      >
                                        {showCurrentPassword ? (
                                          <EyeOff className="h-4 w-4" />
                                        ) : (
                                          <Eye className="h-4 w-4" />
                                        )}
                                      </Button>
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={passwordForm.control}
                              name="newPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Nouveau mot de passe</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        {...field}
                                        type={
                                          showNewPassword ? "text" : "password"
                                        }
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3"
                                        onClick={() =>
                                          setShowNewPassword(!showNewPassword)
                                        }
                                      >
                                        {showNewPassword ? (
                                          <EyeOff className="h-4 w-4" />
                                        ) : (
                                          <Eye className="h-4 w-4" />
                                        )}
                                      </Button>
                                    </div>
                                  </FormControl>
                                  {passwordStrength && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between text-sm">
                                        <span>Force du mot de passe:</span>
                                        <span
                                          className={passwordStrength.color.replace(
                                            "bg-",
                                            "text-"
                                          )}
                                        >
                                          {passwordStrength.level}
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                          className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                          style={{
                                            width: `${passwordStrength.percent}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={passwordForm.control}
                              name="confirmPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    Confirmer le nouveau mot de passe
                                  </FormLabel>
                                  <FormControl>
                                    <Input {...field} type="password" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="flex justify-end gap-2 pt-4">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowPasswordDialog(false)}
                              >
                                Annuler
                              </Button>
                              <Button type="submit">Mettre à jour</Button>
                            </div>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-gray-500" />
                      <div>
                        <h4 className="font-medium">
                          Authentification à deux facteurs
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {user?.two_factor_enabled ? "Activée" : "Désactivée"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={user?.two_factor_enabled || false}
                      onCheckedChange={(checked) => {
                        // Handle 2FA toggle
                        toast({
                          title: checked ? "2FA Activée" : "2FA Désactivée",
                          description: checked
                            ? "Authentification à deux facteurs activée"
                            : "Authentification à deux facteurs désactivée",
                        });
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-6 p-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Préférences de notification
                  </CardTitle>
                  <CardDescription>
                    Choisissez comment vous souhaitez être notifié
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Notifications par email</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recevoir des notifications par email
                        </p>
                      </div>
                      <Switch
                        checked={notifications.email}
                        onCheckedChange={(checked) =>
                          handleNotificationUpdate("email", checked)
                        }
                      />
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Notifications push</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recevoir des notifications dans le navigateur
                        </p>
                      </div>
                      <Switch
                        checked={notifications.push}
                        onCheckedChange={(checked) =>
                          handleNotificationUpdate("push", checked)
                        }
                      />
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Formations</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Notifications sur les nouvelles formations
                        </p>
                      </div>
                      <Switch
                        checked={notifications.training}
                        onCheckedChange={(checked) =>
                          handleNotificationUpdate("training", checked)
                        }
                      />
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Demandes de congé</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Notifications sur les demandes de congé
                        </p>
                      </div>
                      <Switch
                        checked={notifications.leave}
                        onCheckedChange={(checked) =>
                          handleNotificationUpdate("leave", checked)
                        }
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Preferences Tab */}
            <TabsContent value="preferences" className="space-y-6 p-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Préférences d'affichage
                  </CardTitle>
                  <CardDescription>
                    Personnalisez l'apparence de l'application
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Thème</Label>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Choisissez l'apparence de l'interface
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <Button
                          variant={theme === "light" ? "default" : "outline"}
                          className="flex items-center gap-2 h-12"
                          onClick={() => setTheme("light")}
                        >
                          <Sun className="h-4 w-4" />
                          Clair
                        </Button>
                        <Button
                          variant={theme === "dark" ? "default" : "outline"}
                          className="flex items-center gap-2 h-12"
                          onClick={() => setTheme("dark")}
                        >
                          <Moon className="h-4 w-4" />
                          Sombre
                        </Button>
                        <Button
                          variant={theme === "system" ? "default" : "outline"}
                          className="flex items-center gap-2 h-12"
                          onClick={() => setTheme("system")}
                        >
                          <Monitor className="h-4 w-4" />
                          Système
                        </Button>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Langue</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Français (France)
                        </p>
                      </div>
                      <Button variant="outline" disabled>
                        Bientôt disponible
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Informations sur le compte</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-600 dark:text-gray-400">
                        Date d'embauche
                      </Label>
                      <p className="font-medium">
                        {user?.date_embauche
                          ? new Date(user.date_embauche).toLocaleDateString(
                              "fr-FR"
                            )
                          : "Non renseigné"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-600 dark:text-gray-400">
                        Dernière connexion
                      </Label>
                      <p className="font-medium">
                        {user?.derniere_connexion
                          ? new Date(
                              user.derniere_connexion
                            ).toLocaleDateString("fr-FR")
                          : "Première connexion"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-600 dark:text-gray-400">
                        Département
                      </Label>
                      <p className="font-medium">
                        {user?.departement?.nom || "Non assigné"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-600 dark:text-gray-400">
                        Type de contrat
                      </Label>
                      <p className="font-medium">
                        {user?.type_contrat?.toUpperCase() || "Non défini"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
